import isNaN from 'lodash/isNaN';
import sortBy from 'lodash/sortBy';
import isEmpty from 'lodash/isEmpty';
import { applyToPoint, inverse } from 'transformation-matrix';
import { inject, injectable } from 'inversify';
import Messager from '../messager';
import IDS from '../../constants/IDS';
import { createCanvas, isCanvasSupported } from '../../helpers/canvas';
import {
  findExtremeCoordinates,
  getBoundingClientRect,
} from '../../helpers/coordinates';
import { isSafari } from '../../helpers/device';
import { createSeat, filterTickets, findSeatNearByCursor } from '../../helpers/seats';
import { trackTransforms } from '../../helpers/transform';
import { getWidgetParams, transformWidgetSchemeData } from '../../helpers/widget';
import widgetSources from '../../sources/widget';
import { Settings } from '../../types/settings';
import { ITicket } from '../../types/ticket';
import {
  CATEGORIES_EVENTS,
  PRICES_EVENTS,
  ZOOM_EVENTS,
} from '../../constants/CONTROLLERS_EVENT_TYPES';

import WidgetSkeleton from '../widget/WidgetSkeleton';

import { ZOOM_MODULE_IDS } from './zoom/zoom.types';
import { TICKETS_POPOVER_MODULE_IDS } from '../widget/ticketsPopover/ticketsPopover.module';
import { TICKETS_MODULE_IDS } from './tickets/tickets.types';
import { PRICES_MODULE_IDS } from '../widget/prices/prices.module';
import { POPOVER_MODULE_IDS } from '../widget/popover/popover.types';
import { NOTIFICATION_MODULE_IDS } from '../widget/notification/notification.module';
import { HELP_MODULE_IDS } from '../widget/help/help.module';
import { CATEGORIES_MODULE_IDS } from './categories/categories.module';
import { CALENDAR_MODULE_IDS } from '../widget/calendar/calendar.types';
import { BACKGROUND_MODULE_IDS } from './background/background.types';
import { ANNOUNCEMENTS_MODULE_IDS } from './announcements/announcements.const';
// @ts-ignore
import { schemDataAdapter } from '../../helpers/adapters';
// @ts-ignore
import { sectorsMockObj } from '../../../moks/tickets';
import { captureException } from '../../helpers/sentry';
import MESSAGE_TYPES from '../../constants/MESSAGE_TYPES';
import type { IBackgroundController } from './background/background.interface';
import type { ITicketsController } from './tickets/tickets.interface';
import type { IZoomController } from './zoom/zoom.interface';
import type { ICalendarController } from '../widget/calendar/calendar.interface';
import type { IPopoverController } from './popover/popover.interface';
import { STANDING_TICKETS_MODULE_IDS } from './standingTickets/standingTickets.const';
import { validateSchema, validateSettings } from '../../shared/helpers/schema';

const isIE11 = !!window.MSInputMethodContext && !!document.documentMode;

@injectable()
export class Widget extends WidgetSkeleton {
  seats: Array<any> = [];
  currentTickets: Array<any> = [];
  startX: number = 0;
  startY: number = 0;
  schemeWidth: number = 0;
  schemeHeight: number = 0;
  /**
   * css left of staticCanvas
   */
  offsetLeft: number;
  focusedSeat: ITicket;
  isHybrid: boolean = false;
  schemeSupported = isCanvasSupported();
  schemeMode = this.schemeSupported;
  withCategories: boolean = false;
  svgNode: null | SVGAElement = null; // используется, возможно можно отрефакторить и работать через background модуль или вообще использовать dom api
  dragXoffset = 0;
  dragYoffset = 0;

  @inject(ZOOM_MODULE_IDS.ZOOM_CONTROLLER) public zoomController: IZoomController;
  @inject(TICKETS_POPOVER_MODULE_IDS.TICKETS_POPOVER_CONTROLLER) public ticketsPopoverController;
  @inject(TICKETS_MODULE_IDS.TICKETS_CONTROLLER) public ticketsController: ITicketsController;
  @inject(PRICES_MODULE_IDS.PRICES_CONTROLLER) public pricesController;
  @inject(POPOVER_MODULE_IDS.POPOVER_CONTROLLER) public popoverController: IPopoverController;
  @inject(NOTIFICATION_MODULE_IDS.NOTIFICATION_CONTROLLER) public notificationController;
  @inject(HELP_MODULE_IDS.HELP_CONTROLLER) public helpController;
  @inject(CATEGORIES_MODULE_IDS.CATEGORIES_CONTROLLER) public categoriesController;
  @inject(CALENDAR_MODULE_IDS.CALENDAR_CONTROLLER) public calendarController: ICalendarController;
  @inject(BACKGROUND_MODULE_IDS.BACKGROUND_CONTROLLER)
  public backgroundController: IBackgroundController;
  @inject(ANNOUNCEMENTS_MODULE_IDS.ANNOUNCEMENTS_CONTROLLER) public announcementsController;
  @inject(STANDING_TICKETS_MODULE_IDS.STANDING_TICKETS_CONTROLLER) public standingTicketsController;

  setSettings({ uuid, settings }: { uuid: string; settings: Settings }) {
    super.setSettings({ uuid, settings });

    this.ticketsController.addListener(this.ticketsController.events.ADD, (newTicket) => {
      if (!this.schemeMode) return;
      this.renderSelectedSeats({
        newTicket,
      });
    });
    this.ticketsController.addListener(this.ticketsController.events.REMOVE, (removedTicket) => {
      if (!this.schemeMode) return;
      this.renderSelectedSeats();
      this.renderLeftovers();

      this.renderBackgroundOutlines();

      if (!this.isMobile && removedTicket) {
        createSeat({
          ctx: this.seatsCtx,
          seat: removedTicket,
          hovered: true,
        });
      }
    });
    this.stateController.addListener(this.stateController.events.UPDATE, (state) => {
      if (this.schemeSupported) {
        if (!state.isDragging && !state.isLoading && state.schemeIsLoaded) {
          this.update();
        }
      }
    });
    this.stateController.addListener(
      this.stateController.events.SHCEME_DATA_LOADED,
      this.renderSeatsPoints,
    );
    this.stateController.addListener(
      this.stateController.events.TICKETS_DATA_LOADED,
      this.renderTickets,
    );
    this.ticketsController.addListener(this.ticketsController.events.SET_PRICES, this.setPrices);
    this.categoriesController.addListener(CATEGORIES_EVENTS.SET_PRICES, this.setCategoriesPrices);
    this.pricesController.addListener(PRICES_EVENTS.TOGGLE_PRICE, this.updateSeatsColor);
    this.ticketsController.addListener(
      this.ticketsController.events.RENDER_TICKETS,
      this.renderTickets,
    );
  }

  createSchemeCanvas(layout: HTMLElement) {
    this.staticCanvas = createCanvas(IDS.staticCanvas);
    this.seatsCanvas = createCanvas(IDS.canvas);
    const fragment = document.createDocumentFragment();
    const rootLayout = getBoundingClientRect(layout);

    window.Widget.rootLayout = rootLayout;
    window.Widget.width = rootLayout.width * this.dpr;
    window.Widget.height = rootLayout.height * this.dpr;

    this.offsetLeft = rootLayout.left;

    fragment.appendChild(this.staticCanvas);
    fragment.appendChild(this.seatsCanvas);
    layout.prepend(fragment);

    const { top: offsetTop } = getBoundingClientRect(this.staticCanvas);

    const widgetWidth = rootLayout.width * this.dpr;
    const widgetHeight = rootLayout.height * this.dpr;

    this.staticCanvas.style.width = `${widgetWidth / this.dpr}px`;
    this.staticCanvas.style.height = `${widgetHeight / this.dpr}px`;
    this.seatsCanvas.style.width = `${widgetWidth / this.dpr}px`;
    this.seatsCanvas.style.height = `${widgetHeight / this.dpr}px`;
    this.staticCanvas.setAttribute('width', `${widgetWidth}`);
    this.staticCanvas.setAttribute('height', `${widgetHeight}`);
    this.seatsCanvas.setAttribute('width', `${widgetWidth}`);
    this.seatsCanvas.setAttribute('height', `${widgetHeight}`);

    this.staticCtx = this.staticCanvas.getContext('2d');
    this.seatsCtx = this.seatsCanvas.getContext('2d');
    this.ctxs = [this.staticCtx, this.seatsCtx];
    this.offsetTop = offsetTop;

    trackTransforms(this.staticCtx);
    trackTransforms(this.seatsCtx);

    this.backgroundController.setBackgroundCtx(this.staticCtx);
  }

  loadAnnouncements = () => this.announcementsController.loadAnnouncements();
  loadStandingTickets = () => this.standingTicketsController.loadStandingTickets();

  loadData = async () => {
    Promise.all([
      this.loadScheme().then(async (schemeData) => {
        const datePrices = await this.loadDatePrices();
        this.metaController.addMeta({
          ...schemeData,
          prices: datePrices,
        });

        await this.onSchemeDataLoad();

        return schemeData;
      }),
      this.ticketsController.getTickets(),
    ]).then(([schemeData]) => {
      this.stateController.emit(
        this.stateController.events.SHCEME_DATA_LOADED,
        schemeData.coordinates,
      );
      this.stateController.emit(this.stateController.events.TICKETS_DATA_LOADED);
      this.backgroundController.init();
      this.stateController.emit(this.stateController.events.DATA_LOADED);
    });
  };

  loadDatePrices = async () => {
    try {
      const data = await widgetSources.datesPrices();

      return data;
    } catch (error) {
      captureException(error);
    }
  };

  loadScheme = async () => {
    try {
      validateSettings();

      const response = await widgetSources.root();

      validateSchema(response.data);

      const schemeData = transformWidgetSchemeData({
        settings: this.settings,
        schemeData: response.data,
      });
      this.setSchemeParameters(schemeData);

      const { coordinates } = schemeData;
      this.staticSeats = isEmpty(coordinates) ? [] : coordinates;

      return schemeData;
    } catch (error) {
      this.ticketsController.emit(this.ticketsController.events.CHECK_ERROR, error);
    }
  };

  onSchemeDataLoad = async () => {
    if (isSafari()) {
      await this.backgroundController.createSVGBackground();
    } else {
      await this.backgroundController.preloadImageBackground();
    }

    this.backgroundController.render();
  };

  setSchemeParameters = (data: any) => {
    let minX = 0;
    let minY = 0;
    let maxX = Number(data.width);
    let maxY = Number(data.height);

    if (isNaN(maxX) || isNaN(maxY)) {
      ({ minX, minY, maxX, maxY } = findExtremeCoordinates(data.coordinates));
    }

    const { zoomRange, playgroundHeight, playgroundWidth, startX, startY, isStadium } =
      getWidgetParams({
        width: window.Widget.width,
        height: window.Widget.height,
        settings: this.settings,
        dpr: this.dpr,
        isMobile: this.isMobile,
        coords: {
          minX,
          minY,
          maxX,
          maxY,
        },
      });

    const schemeWidth = (maxX - minX) * this.dpr;
    const schemeHeight = (maxY - minY) * this.dpr;

    let adoptedMetaForNewWidget = {};

    if (window.Widget.settings.eventData) {
      const { eventData } = window.Widget.settings;

      adoptedMetaForNewWidget = {
        hall_layout: {
          meta: {
            bg: data.bg,
            width: data.width,
            height: data.height,
          },
        },
        title: eventData.title,
        bg: data.bg,
        width: data.width,
        height: data.height,
        dates: window.Widget.settings.dates,
      };
    }

    this.schemeParameters = {
      minX,
      minY,
      maxX,
      maxY,
      playgroundHeight,
      playgroundWidth,
      schemeWidth,
      schemeHeight,
      startX,
      startY,
      isStadium,
      hallLayoutMeta: {
        ...(data.meta && { ...data.meta.hall_layout.meta }),
        ...adoptedMetaForNewWidget,
      },
    };
    this.width = window.Widget.width;
    this.height = window.Widget.height;
    this.setSchemeSizes(schemeWidth, schemeHeight);
    this.setZoomRange(zoomRange);
    this.setStartCoordinates(startX, startY);
    this.scale(zoomRange[0]);
  };

  toggleFullscreen = () => {
    const { isFullscreen: prevFullScreen } = this.stateController;

    this.stateController.lock();

    this.stateController.setState({
      isFullscreen: !prevFullScreen,
    });

    Messager.send({
      type: MESSAGE_TYPES.FULLSCREEN,
      isFullscreen: this.stateController.state.isFullscreen,
    });
  };

  get ticketsInViewport() {
    if (this.zoomController.zoomIsMinimum) {
      return this.ticketsController.tickets;
    }
    const { x, y, x2, y2 } = this.getViewport();
    return filterTickets({
      tickets: this.ticketsController.tickets,
      x,
      y,
      x2,
      y2,
    });
  }

  resetTransforms() {
    this.ctxs.forEach((ctx) => {
      ctx.setTransform(1, 0, 0, 1, 0, 0);
      ctx.clearRect(0, 0, this.width * this.dpr, this.height * this.dpr);
    });
  }

  clear() {
    this.ctxs.forEach((ctx) => {
      ctx.save();
      ctx.setTransform(1, 0, 0, 1, 0, 0);
      ctx.clearRect(0, 0, this.width * this.dpr, this.height * this.dpr);
      ctx.restore();
    });
  }

  clearDynamicCtx() {
    this.seatsCtx.save();
    this.seatsCtx.setTransform(1, 0, 0, 1, 0, 0);
    this.seatsCtx.clearRect(0, 0, this.width * this.dpr, this.height * this.dpr);
    this.seatsCtx.restore();
  }

  clearStaticCtx() {
    this.staticCtx.save();
    this.staticCtx.setTransform(1, 0, 0, 1, 0, 0);
    this.staticCtx.clearRect(0, 0, this.width * this.dpr, this.height * this.dpr);
    this.staticCtx.restore();
  }

  setStartCoordinates(x, y) {
    this.startX = x;
    this.startY = y;

    this.translate(x, y);
  }

  findAndRenderSeat({ pageX, pageY, withoutRender = false }) {
    const matrix = this.staticCtx.getTransform();
    const point = applyToPoint(inverse(matrix), {
      x: pageX * this.dpr - this.offsetLeft * this.dpr,
      y: pageY * this.dpr - this.offsetTop * this.dpr,
    });
    const nearTicket = findSeatNearByCursor({ tickets: this.currentTickets, point });

    if (this.focusedSeat) {
      this.renderSelectedSeats();
    }

    if (nearTicket) {
      this.focusedSeat = nearTicket;

      if (!withoutRender) {
        createSeat({
          ctx: this.seatsCtx,
          seat: nearTicket,
          hovered: true,
          isSelected: this.ticketsController.selectedTicketsIds.has(nearTicket.id),
        });
      }

      return this.focusedSeat;
    }

    this.focusedSeat = null;

    return this.focusedSeat;
  }

  renderTickets = () => {
    if (!this.schemeMode) return;

    const { ticketsInViewport } = this;
    const { selectedPrices } = this.pricesController;
    this.currentTickets = ticketsInViewport;
    const { isStadium } = this.schemeParameters;
    const { zoomIsMinimum } = this.zoomController;
    ticketsInViewport.forEach((seat) => {
      createSeat({
        seat,
        selectedPrices,
        zoomIsMinimum,
        isStadium,
        ctx: this.staticCtx,
        active: true,
      });
    });
    this.renderSelectedSeats();
    this.renderLeftovers();

    !this.isHybrid && this.renderBackgroundOutlines();
  };

  renderLeftovers() {
    if (this.zoomController.zoomIsMinimum) {
      this.backgroundController.renderLeftovers(this.ticketsController.leftoversSectors);
    }
  }

  renderSelectedSeats = ({ newTicket } = { newTicket: null }) => {
    this.clearDynamicCtx();
    const {
      selectedEvent,
      meta: { isPeriodical },
    } = this.metaController;
    const { selectedTickets, selectedTicketsIds } = this.ticketsController;
    const filteredSelectedTickets = isPeriodical
      ? selectedTickets.filter((ticket) => ticket.selectedEvent === selectedEvent)
      : selectedTickets;

    sortBy(filteredSelectedTickets, ['row', 'seat']).forEach((seat) => {
      createSeat({
        seat,
        ctx: this.seatsCtx,
        hovered: !this.isMobile && newTicket && seat.id === newTicket.id,
        isSelected: selectedTicketsIds.has(seat.id),
      });
    });

    this.isHybrid && this.renderBackgroundOutlines();
  };

  renderBackgroundOutlines() {
    this.backgroundController.renderCategoriesOutlines();
  }

  updateSeatsColor = () => {
    this.update(true);
  };

  update(onlySeats = false) {
    if (isIE11) {
      return setTimeout(() => {
        this.updateLayout(onlySeats);
      }, 0);
    }
    this.updateLayout(onlySeats);
  }

  updateLayout(onlySeats = false) {
    if (!onlySeats) {
      this.backgroundController.render();
      this.renderSeatsPoints(this.staticSeatsInViewport);
    }

    this.renderTickets();
  }

  setSchemeSizes(width, height) {
    this.schemeWidth = width;
    this.schemeHeight = height;
  }

  setPrices = (prices) => {
    this.pricesController.updatePrices(prices);
  };

  setCategoriesPrices = (prices, colors) => {
    this.pricesController.updateCategoriesPrices(prices, colors);
  };

  resize = ({ offsetTop, offsetLeft, isFullscreen, widgetWidth, widgetHeight }) => {
    if (this.stateController.state.isFailure) return;
    const matrix = this.staticCtx.getTransform();

    const prevParams = {
      matrix,
      width: this.width,
      height: this.height,
      startX: this.startX,
      startY: this.startY,
      zoomRange: this.zoomController.zoomRange,
    };

    this.width = widgetWidth * this.dpr;
    this.height = widgetHeight * this.dpr;

    this.offsetTop = offsetTop;
    this.offsetLeft = offsetLeft;
    const { minX, minY, maxX, maxY } = this.schemeParameters;

    this.staticCanvas.style.width = `${widgetWidth}px`;
    this.staticCanvas.style.height = `${widgetHeight}px`;
    this.seatsCanvas.style.width = `${widgetWidth}px`;
    this.seatsCanvas.style.height = `${widgetHeight}px`;
    this.staticCanvas.setAttribute('width', `${this.width}`);
    this.staticCanvas.setAttribute('height', `${this.height}`);
    this.seatsCanvas.setAttribute('width', `${this.width}`);
    this.seatsCanvas.setAttribute('height', `${this.height}`);

    const {
      zoomRange,
      playgroundHeight,
      playgroundWidth,
      startX,
      startY,
    } = getWidgetParams({
      width: this.width,
      height: this.height,
      isFullscreen,
      dpr: this.dpr,
      settings: this.settings,
      isMobile: this.isMobile,
      coords: {
        minX,
        minY,
        maxX,
        maxY,
      },
      prevParams,
    });

    this.schemeParameters = {
      ...this.schemeParameters,
      playgroundHeight,
      playgroundWidth,
      startX,
      startY,
    };

    this.resetStyles();
    this.resetTransforms();

    const isZoomRangesEquals = prevParams.zoomRange.every(
      (rangeItem, idx) => rangeItem === zoomRange[idx],
    );
    if (!isZoomRangesEquals) {
      this.setZoomRange(zoomRange);
    }

    const [newScale] = this.zoomController.zoomRange;
    this.setStartCoordinates(startX, startY);
    this.scale(newScale);

    this.update();
    this.backgroundController.forceUpdate();
    this.zoomController.emit(ZOOM_EVENTS.UPDATE);
  };

  setSchemeMode(val) {
    this.schemeMode = val;
    this.stateController.emit(this.stateController.events.SCHEME_MODE_CHANGE);
  }

  isPossibleToShowAndSelectSeat() {
    return this.zoomController.zoomIsMaximum;
  }
}
